import { useEffect, useState } from "react";
import { KeyRound, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@bookdental/ui";
import { trpc } from "@/lib/trpc";
import { AJUDA_ORGANIZACAO_E_CONTA } from "@/lib/ajuda/dialogos/organizacao-e-conta";
import type { ModoDoPin } from "@/lib/pin-link";

type Tela = ModoDoPin;

const campo =
  "h-11 w-full rounded-md border border-zinc-200 bg-white px-3 text-center text-lg tracking-[0.4em] dark:border-border dark:bg-card";

const soNumeros = (v: string) => v.replace(/\D/g, "").slice(0, 6);

/** Pedir, criar ou redefinir o PIN. "Esqueci o PIN" redefine com a senha da conta. */
export function PinDialog({ modo, onFechar }: { modo: ModoDoPin | null; onFechar: (ok: boolean) => void }) {
  const utils = trpc.useUtils();
  const [tela, setTela] = useState<Tela>("confirmar");
  const [pin, setPin] = useState("");
  const [repetido, setRepetido] = useState("");
  const [senha, setSenha] = useState("");
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    if (modo) {
      setTela(modo);
      setPin("");
      setRepetido("");
      setSenha("");
      setErro(null);
    }
  }, [modo]);

  const ok = () => {
    utils.pin.status.invalidate();
    onFechar(true);
  };
  const falhou = (e: { message: string }) => setErro(e.message);
  const confirmar = trpc.pin.confirmar.useMutation({ onSuccess: ok, onError: falhou });
  const definir = trpc.pin.definir.useMutation({ onSuccess: ok, onError: falhou });
  const ocupado = confirmar.isPending || definir.isPending;

  const enviar = (e: React.FormEvent) => {
    e.preventDefault();
    setErro(null);
    if (tela === "confirmar") return confirmar.mutate({ pin });
    if (pin.length < 4) return setErro("O PIN tem de 4 a 6 números.");
    if (pin !== repetido) return setErro("Os dois PINs não batem.");
    definir.mutate(tela === "redefinir" ? { pin, senha } : { pin });
  };

  const titulo =
    tela === "confirmar" ? "Digite seu PIN" : tela === "criar" ? "Crie seu PIN" : "Redefinir o PIN";
  const explicacao =
    tela === "confirmar"
      ? "Esta ação pede o PIN. Ele vale por 10 minutos depois de digitado."
      : tela === "criar"
        ? "O PIN protege ações sensíveis, como mudar preços e transferir a organização. De 4 a 6 números."
        : "Confirme a senha da sua conta e escolha um PIN novo.";

  return (
    <Dialog open={!!modo} onOpenChange={(v) => !v && onFechar(false)}>
      <DialogContent className="max-w-sm border border-zinc-200 bg-white shadow-2xl dark:border-border dark:bg-card">
        <div className="flex flex-col items-center gap-2 text-center">
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-brand-50 text-brand-700 dark:bg-primary/10 dark:text-primary">
            <KeyRound className="h-5 w-5" />
          </span>
          <DialogTitle ajuda={AJUDA_ORGANIZACAO_E_CONTA.pin} classeDoGrupo="justify-center">
            {titulo}
          </DialogTitle>
          <DialogDescription className="text-[13px]">{explicacao}</DialogDescription>
        </div>
        <form onSubmit={enviar} className="space-y-3">
          {tela === "redefinir" && (
            <input
              type="password"
              autoComplete="current-password"
              placeholder="Senha da conta"
              aria-label="Senha da conta"
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
              className="h-11 w-full rounded-md border border-zinc-200 bg-white px-3 text-sm dark:border-border dark:bg-card"
            />
          )}
          <input
            autoFocus
            type="password"
            inputMode="numeric"
            autoComplete="off"
            aria-label={tela === "confirmar" ? "PIN" : "Novo PIN"}
            placeholder="••••"
            value={pin}
            onChange={(e) => setPin(soNumeros(e.target.value))}
            className={campo}
          />
          {tela !== "confirmar" && (
            <input
              type="password"
              inputMode="numeric"
              autoComplete="off"
              aria-label="Repita o PIN"
              placeholder="Repita o PIN"
              value={repetido}
              onChange={(e) => setRepetido(soNumeros(e.target.value))}
              className={campo}
            />
          )}
          {erro && <p className="text-center text-[12.5px] text-red-600">{erro}</p>}
          <button
            type="submit"
            disabled={ocupado || pin.length < 4}
            className="inline-flex h-10 w-full items-center justify-center gap-1.5 rounded-md bg-brand-600 text-[13px] font-semibold text-white hover:bg-brand-700 disabled:opacity-60"
          >
            {ocupado && <Loader2 className="h-4 w-4 animate-spin" />}
            {tela === "confirmar" ? "Confirmar" : "Salvar PIN"}
          </button>
          {tela === "confirmar" && (
            <button
              type="button"
              onClick={() => {
                setTela("redefinir");
                setPin("");
                setErro(null);
              }}
              className="w-full text-center text-[12.5px] font-medium text-brand-700 hover:underline dark:text-primary"
            >
              Esqueci o PIN
            </button>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
}
